import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  CircularProgress,
  Select,
  FormControl,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ConstructionIcon from '@mui/icons-material/Construction';
import Paper from '@mui/material/Paper';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams, GridApi } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import MenuItem from '@mui/material/MenuItem';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Client, Employee, Trade, DEFAULT_COMMISSION_RATES } from '../types';
import { clientService } from '../services/client.service';
import { tradeService } from '../services/trade.service';
import { salaryService } from '../services/salary.service';
import { useAuth } from '../contexts/AuthContext';
import { useReadOnlyAction } from '../hooks/useReadOnlyAction';
import { formatCurrency, formatDate, toTitleCase } from '../utils/formatters';
import PageHeader from '../components/common/PageHeader';
import Skeleton from '@mui/material/Skeleton';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CreateClientForm {
  name: string;
  initialCapital: string;
  brokerClientCode: string;
  stockRate: string;
  optionRate: string;
  intraStockRate: string;
  futureRate: string;
  referredByEmployeeId: string;
  commStockRate: string;
  commIntradayRate: string;
  commFutStkRate: string;
  commOptionRate: string;
}

interface ClientTradeStats {
  openCount: number;
  realizedPnL: number;
  unrealizedPnL: number;
}

interface EnrichedClient extends Client {
  openTrades: number;
  realizedPnL: number;
  unrealizedPnL: number;
  totalPnL: number;
  runningCapital: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ClientsList() {
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin, isHR } = useAuth();
  const { isReadOnly, snackbarOpen, closeSnackbar, message, showWorkInProgress } = useReadOnlyAction('clients');

  // Data state
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<Client[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [deletingClient, setDeletingClient] = useState(false);
  const canDelete = isAdmin || isSuperAdmin; // hard-delete: admin / super-admin only (not HR)

  const handleConfirmDeleteClient = async () => {
    if (!clientToDelete) return;
    setDeletingClient(true);
    try {
      await clientService.deleteClient(clientToDelete.clientId);
      setSuccess(`Client "${clientToDelete.name}" deleted`);
      setClientToDelete(null);
      fetchClients();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err.message || 'Failed to delete client');
    } finally {
      setDeletingClient(false);
    }
  };

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [agentFilter, setAgentFilter] = useState<string>('all');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [formData, setFormData] = useState<CreateClientForm>({
    name: '',
    initialCapital: '',
    brokerClientCode: '',
    stockRate: '0.3',
    optionRate: '100',
    intraStockRate: '0.03',
    futureRate: '0.03',
    referredByEmployeeId: '',
    commStockRate: DEFAULT_COMMISSION_RATES.stock.toString(),
    commIntradayRate: DEFAULT_COMMISSION_RATES.intraday.toString(),
    commFutStkRate: DEFAULT_COMMISSION_RATES.futStk.toString(),
    commOptionRate: DEFAULT_COMMISSION_RATES.option.toString(),
  });

  const gridRef = useRef<AgGridReact<EnrichedClient>>(null);

  // Admin always sees action buttons, but in read-only mode they show "Work in Progress"
  const showActionButtons = isAdmin || isSuperAdmin || isHR;

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchClients = async () => {
    try {
      setLoading(true);
      const data = await clientService.getClients();
      setClients(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrades = async () => {
    try {
      const data = await tradeService.getTrades();
      setTrades(data);
    } catch {
      // Non-critical — grid will show zeros for trade stats
    }
  };

  useEffect(() => {
    fetchClients();
    fetchTrades();
    salaryService.getEmployees(true).then((data: any) => setEmployees(data.employees || data || [])).catch(() => {});
  }, []);

  // ---------------------------------------------------------------------------
  // Trade stats per client
  // ---------------------------------------------------------------------------

  const tradeStatsByClient = useMemo<Record<string, ClientTradeStats>>(() => {
    const map: Record<string, ClientTradeStats> = {};
    for (const t of trades) {
      if (t.isDeleted) continue;
      if (!map[t.clientId]) {
        map[t.clientId] = { openCount: 0, realizedPnL: 0, unrealizedPnL: 0 };
      }
      const entry = map[t.clientId];
      entry.realizedPnL += t.realizedPnL || 0;
      if (t.status === 'OPEN') {
        entry.openCount += 1;
        entry.unrealizedPnL += t.unrealizedPnL || 0;
      }
    }
    return map;
  }, [trades]);

  // ---------------------------------------------------------------------------
  // Enriched rows (clients + trade stats)
  // ---------------------------------------------------------------------------

  const enrichedClients = useMemo<EnrichedClient[]>(() => {
    return clients.map((c) => {
      const stats = tradeStatsByClient[c.clientId] || { openCount: 0, realizedPnL: 0, unrealizedPnL: 0 };
      return {
        ...c,
        openTrades: stats.openCount,
        realizedPnL: stats.realizedPnL,
        unrealizedPnL: stats.unrealizedPnL,
        totalPnL: stats.realizedPnL + stats.unrealizedPnL,
        runningCapital: c.initialCapital + stats.realizedPnL,
      };
    });
  }, [clients, tradeStatsByClient]);

  // ---------------------------------------------------------------------------
  // Filtered rows (status + agent filters, search via AG-Grid quickFilter)
  // ---------------------------------------------------------------------------

  const filteredClients = useMemo<EnrichedClient[]>(() => {
    let list = enrichedClients;
    if (statusFilter === 'active') list = list.filter((c) => c.isActive);
    if (statusFilter === 'inactive') list = list.filter((c) => !c.isActive);
    if (agentFilter !== 'all') list = list.filter((c) => (c.referredByName || '') === agentFilter);
    return list;
  }, [enrichedClients, statusFilter, agentFilter]);

  // Unique agent names for filter dropdown
  const uniqueAgents = useMemo<string[]>(() => {
    const set = new Set<string>();
    clients.forEach((c) => {
      if (c.referredByName) set.add(c.referredByName);
    });
    return Array.from(set).sort();
  }, [clients]);

  // Apply quick filter when search text changes
  useEffect(() => {
    const api: GridApi | undefined = gridRef.current?.api;
    if (api) {
      api.setGridOption('quickFilterText', searchText);
    }
  }, [searchText]);

  // ---------------------------------------------------------------------------
  // Summary metrics
  // ---------------------------------------------------------------------------

  const activeCount = useMemo(() => clients.filter((c) => c.isActive).length, [clients]);
  const totalAUM = useMemo(() => clients.reduce((sum, c) => sum + (c.initialCapital || 0), 0), [clients]);
  const totalRunningCapital = useMemo(
    () => enrichedClients.reduce((sum, c) => sum + c.runningCapital, 0),
    [enrichedClients],
  );
  const totalPnL = useMemo(() => {
    let sum = 0;
    for (const stats of Object.values(tradeStatsByClient)) {
      sum += stats.realizedPnL + stats.unrealizedPnL;
    }
    return sum;
  }, [tradeStatsByClient]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleOpenDialog = () => {
    if (isReadOnly) {
      showWorkInProgress();
      return;
    }
    setDialogOpen(true);
  };

  const handleCreateClient = async () => {
    if (!formData.name || !formData.initialCapital) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setCreating(true);
      await clientService.createClient({
        name: toTitleCase(formData.name),
        initialCapital: parseFloat(formData.initialCapital),
        brokerClientCode: formData.brokerClientCode.trim() || undefined,
        brokerageRates: {
          stock: parseFloat(formData.stockRate) / 100 || 0.003,
          option: parseFloat(formData.optionRate) || 100,
          intraStock: parseFloat(formData.intraStockRate) / 100 || 0.0003,
          future: parseFloat(formData.futureRate) / 100 || 0.0003,
        },
        referredByEmployeeId: formData.referredByEmployeeId || undefined,
        commissionRates: formData.referredByEmployeeId
          ? {
              stock: parseFloat(formData.commStockRate) || DEFAULT_COMMISSION_RATES.stock,
              intraday: parseFloat(formData.commIntradayRate) || DEFAULT_COMMISSION_RATES.intraday,
              futStk: parseFloat(formData.commFutStkRate) || DEFAULT_COMMISSION_RATES.futStk,
              option: parseFloat(formData.commOptionRate) || DEFAULT_COMMISSION_RATES.option,
            }
          : undefined,
      });
      setSuccess(`Client "${formData.name}" created successfully!`);
      setDialogOpen(false);
      setFormData({
        name: '',
        initialCapital: '',
        brokerClientCode: '',
        stockRate: '0.3',
        optionRate: '100',
        intraStockRate: '0.03',
        futureRate: '0.03',
        referredByEmployeeId: '',
        commStockRate: DEFAULT_COMMISSION_RATES.stock.toString(),
        commIntradayRate: DEFAULT_COMMISSION_RATES.intraday.toString(),
        commFutStkRate: DEFAULT_COMMISSION_RATES.futStk.toString(),
        commOptionRate: DEFAULT_COMMISSION_RATES.option.toString(),
      });
      fetchClients();
      fetchTrades();
    } catch (err: any) {
      setError(err.message || 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  // ---------------------------------------------------------------------------
  // P&L cell helper
  // ---------------------------------------------------------------------------

  const pnlColor = useCallback((value: number) => {
    if (value > 0) return '#059669';
    if (value < 0) return '#DC2626';
    return '#0F172A';
  }, []);

  // Deterministic gradient seed for client avatars — gives each client a
  // stable, unique initial tile while staying in the premium palette.
  const avatarStyle = useCallback((seed: string) => {
    const gradients: [string, string][] = [
      ['#4F46E5', '#7C3AED'], // indigo → violet
      ['#2563EB', '#0891B2'], // blue → cyan
      ['#059669', '#0D9488'], // emerald → teal
      ['#D97706', '#EA580C'], // amber → orange
      ['#DB2777', '#C026D3'], // pink → fuchsia
      ['#3730A3', '#1E40AF'], // indigo-dark → blue-dark
    ];
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    const [a, b] = gradients[h % gradients.length];
    return { background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)` };
  }, []);

  // ---------------------------------------------------------------------------
  // Column definitions
  // ---------------------------------------------------------------------------

  const columnDefs = useMemo<ColDef<EnrichedClient>[]>(
    () => [
      {
        headerName: 'Client',
        field: 'name',
        flex: 1.5,
        minWidth: 180,
        sortable: true,
        filter: true,
        cellRenderer: (params: ICellRendererParams<EnrichedClient>) => {
          const c = params.data;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  ...avatarStyle(c?.clientId || params.value || ''),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  letterSpacing: '-0.01em',
                  flexShrink: 0,
                }}
              >
                {params.value?.charAt(0)?.toUpperCase()}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.88rem',
                    fontWeight: 600,
                    color: '#0F172A',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.25,
                  }}
                  title={params.value}
                >
                  {params.value}
                </Typography>
                {c?.referredByName && (
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: '#94A3B8',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    via {c.referredByName}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        },
      },
      {
        headerName: 'Initial Capital',
        field: 'initialCapital',
        width: 140,
        sortable: true,
        type: 'rightAligned',
        cellRenderer: (params: ICellRendererParams<EnrichedClient>) => (
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#0F172A', fontFeatureSettings: '"tnum"' }}>
            {formatCurrency(params.value)}
          </Typography>
        ),
      },
      {
        headerName: 'Running Capital',
        field: 'runningCapital',
        width: 150,
        sortable: true,
        type: 'rightAligned',
        cellRenderer: (params: ICellRendererParams<EnrichedClient>) => (
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', fontFeatureSettings: '"tnum"' }}>
            {formatCurrency(params.value)}
          </Typography>
        ),
      },
      {
        headerName: 'Open',
        field: 'openTrades',
        width: 86,
        sortable: true,
        type: 'rightAligned',
        cellRenderer: (params: ICellRendererParams<EnrichedClient>) => {
          const val = params.value ?? 0;
          return val > 0 ? (
            <Chip
              label={val}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.75rem',
                fontWeight: 700,
                bgcolor: '#EFF6FF',
                color: '#2563EB',
                fontFeatureSettings: '"tnum"',
                px: 0.5,
              }}
            />
          ) : (
            <Typography sx={{ fontSize: '0.82rem', color: '#94A3B8', fontFeatureSettings: '"tnum"' }}>
              0
            </Typography>
          );
        },
      },
      {
        headerName: 'Realised P&L',
        field: 'realizedPnL',
        width: 130,
        sortable: true,
        type: 'rightAligned',
        cellRenderer: (params: ICellRendererParams<EnrichedClient>) => {
          const val = params.value ?? 0;
          return (
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: pnlColor(val), fontFeatureSettings: '"tnum"' }}>
              {formatCurrency(val)}
            </Typography>
          );
        },
      },
      {
        headerName: 'Unrealised P&L',
        field: 'unrealizedPnL',
        width: 140,
        sortable: true,
        type: 'rightAligned',
        cellRenderer: (params: ICellRendererParams<EnrichedClient>) => {
          const val = params.value ?? 0;
          return (
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: pnlColor(val), fontFeatureSettings: '"tnum"' }}>
              {formatCurrency(val)}
            </Typography>
          );
        },
      },
      {
        headerName: 'Total P&L',
        field: 'totalPnL',
        width: 130,
        sortable: true,
        type: 'rightAligned',
        cellRenderer: (params: ICellRendererParams<EnrichedClient>) => {
          const val = params.value ?? 0;
          return (
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: pnlColor(val), fontFeatureSettings: '"tnum"' }}>
              {formatCurrency(val)}
            </Typography>
          );
        },
      },
      {
        headerName: 'Agent',
        field: 'referredByName',
        width: 130,
        sortable: true,
        filter: true,
        cellRenderer: (params: ICellRendererParams<EnrichedClient>) =>
          params.value ? (
            <Typography sx={{ fontSize: '0.82rem', color: '#334155', fontWeight: 500 }}>
              {params.value}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '0.82rem', color: '#CBD5E1', fontWeight: 500 }}>
              Direct
            </Typography>
          ),
      },
      {
        headerName: 'Status',
        field: 'isActive',
        width: 110,
        cellRenderer: (params: ICellRendererParams<EnrichedClient>) => {
          const isActive = params.value as boolean;
          return (
            <Box display="flex" alignItems="center" gap={0.75}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: isActive ? '#10B981' : '#94A3B8',
                  boxShadow: isActive ? '0 0 0 3px rgba(16, 185, 129, 0.15)' : 'none',
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: isActive ? '#047857' : '#64748B',
                  letterSpacing: '-0.01em',
                }}
              >
                {isActive ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          );
        },
      },
      {
        headerName: 'Joined',
        field: 'createdAt',
        width: 110,
        sortable: true,
        cellRenderer: (params: ICellRendererParams<EnrichedClient>) => (
          <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontFeatureSettings: '"tnum"', fontWeight: 500 }}>
            {formatDate(params.value)}
          </Typography>
        ),
      },
      {
        headerName: '',
        width: canDelete ? 100 : 62,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<EnrichedClient>) => {
          const client = params.data;
          if (!client) return null;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%' }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/clients/${client.clientId}`);
                }}
                sx={{
                  color: '#94A3B8', border: '1px solid #E2E8F0', borderRadius: 1, width: 28, height: 28,
                  '&:hover': { color: '#0A0A0A', borderColor: '#0A0A0A', bgcolor: '#F4F4F5' },
                }}
              >
                <VisibilityIcon sx={{ fontSize: 15 }} />
              </IconButton>
              {canDelete && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setClientToDelete(client as Client);
                  }}
                  sx={{
                    color: '#94A3B8', border: '1px solid #E2E8F0', borderRadius: 1, width: 28, height: 28,
                    '&:hover': { color: '#DC2626', borderColor: '#DC2626', bgcolor: '#FEF2F2' },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 15 }} />
                </IconButton>
              )}
            </Box>
          );
        },
      },
    ],
    [navigate, pnlColor, avatarStyle, canDelete],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ pb: 3 }}>
      <PageHeader
        eyebrow="Management"
        title="Clients"
        subtitle={
          isReadOnly
            ? 'Manage client portfolios and brokerage rates (Read-only mode)'
            : 'Manage client portfolios and brokerage rates'
        }
        actions={
          showActionButtons ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleOpenDialog}
              sx={{
                px: 2,
                py: 0.85,
                borderRadius: '8px',
                bgcolor: '#0A0A0A',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                boxShadow: 'none',
                opacity: isReadOnly ? 0.8 : 1,
                '&:hover': {
                  bgcolor: '#262626',
                  boxShadow: 'none',
                },
              }}
            >
              Add Client
            </Button>
          ) : null
        }
      />

      {/* Initial load skeleton */}
      {loading && clients.length === 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={76}
                sx={{ flex: '1 1 180px', minWidth: 180, borderRadius: '12px' }}
              />
            ))}
          </Box>
          <Skeleton variant="rounded" height={56} sx={{ borderRadius: '12px', mb: 2 }} />
          <Box
            sx={{
              position: 'relative',
              borderRadius: '12px',
              overflow: 'hidden',
              bgcolor: '#FFFFFF',
              border: '1px solid #E2E8F0',
            }}
          >
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={52}
                sx={{
                  bgcolor: i % 2 ? '#F8FAFC' : '#FFFFFF',
                  '&::after': {
                    background:
                      'linear-gradient(90deg, transparent, rgba(236, 72, 153, 0.08), transparent)',
                  },
                }}
              />
            ))}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 2,
                  py: 1,
                  bgcolor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: 999,
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                }}
              >
                <CircularProgress size={16} thickness={5} sx={{ color: '#EC4899' }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569' }}>
                  Loading clients…
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Summary Cards                                                       */}
      {/* ------------------------------------------------------------------ */}
      {!(loading && clients.length === 0) && (<>
      {(() => {
        const pnlPos = totalPnL >= 0;
        const kpis: {
          label: string;
          value: string;
          sub?: string;
          icon: React.ReactNode;
          accent: string;
          accentBg: string;
          valueColor?: string;
        }[] = [
          {
            label: 'Total Clients',
            value: clients.length.toString(),
            sub: `${activeCount} active · ${clients.length - activeCount} inactive`,
            icon: <PeopleOutlineIcon sx={{ fontSize: 20 }} />,
            accent: '#4F46E5',
            accentBg: '#EEF2FF',
          },
          {
            label: 'Total AUM',
            value: formatCurrency(totalAUM),
            sub: 'Sum of initial capital',
            icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20 }} />,
            accent: '#D97706',
            accentBg: '#FEF3C7',
          },
          {
            label: 'Running Capital',
            value: formatCurrency(totalRunningCapital),
            sub: 'Capital + realised P&L',
            icon: <ShowChartIcon sx={{ fontSize: 20 }} />,
            accent: '#2563EB',
            accentBg: '#DBEAFE',
          },
          {
            label: 'Total P&L',
            value: formatCurrency(totalPnL),
            sub: 'Realised + unrealised',
            icon: pnlPos ? <TrendingUpIcon sx={{ fontSize: 20 }} /> : <TrendingDownIcon sx={{ fontSize: 20 }} />,
            accent: pnlPos ? '#059669' : '#DC2626',
            accentBg: pnlPos ? '#D1FAE5' : '#FEE2E2',
            valueColor: totalPnL !== 0 ? (pnlPos ? '#059669' : '#DC2626') : undefined,
          },
          {
            label: 'Active Rate',
            value: clients.length > 0 ? `${Math.round((activeCount / clients.length) * 100)}%` : '—',
            sub: `${activeCount} of ${clients.length}`,
            icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />,
            accent: '#059669',
            accentBg: '#D1FAE5',
          },
        ];
        return (
          <Grid container spacing={2.5} mb={3}>
            {kpis.map((k) => (
              <Grid item xs={6} md={2.4} key={k.label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    transition: 'all 0.2s ease',
                    height: '100%',
                    '&:hover': {
                      borderColor: '#CBD5E1',
                      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.25}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#64748B',
                        letterSpacing: '0.04em',
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      {k.label}
                    </Typography>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1.5,
                        bgcolor: k.accentBg,
                        color: k.accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {k.icon}
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      color: k.valueColor || '#0F172A',
                      letterSpacing: '-0.02em',
                      fontFeatureSettings: '"tnum"',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={k.value}
                  >
                    {k.value}
                  </Typography>
                  {k.sub && (
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: '#64748B',
                        mt: 0.25,
                        fontFeatureSettings: '"tnum"',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={k.sub}
                    >
                      {k.sub}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        );
      })()}

      {/* ------------------------------------------------------------------ */}
      {/* Search & Filter Toolbar                                             */}
      {/* ------------------------------------------------------------------ */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 1.25,
          borderRadius: 2,
          bgcolor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by name, agent, ID…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94A3B8', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 240,
            flex: '1 1 260px',
            maxWidth: 380,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#F8FAFC',
              borderRadius: 1.5,
              fontSize: '0.85rem',
              '& fieldset': { borderColor: '#E2E8F0' },
              '&:hover fieldset': { borderColor: '#CBD5E1' },
              '&.Mui-focused fieldset': { borderColor: '#4F46E5', borderWidth: 1 },
            },
          }}
        />

        <Box sx={{ width: '1px', height: 24, bgcolor: '#E2E8F0', mx: 0.5 }} />

        {/* Segmented status switcher */}
        <Box sx={{ display: 'inline-flex', p: 0.375, bgcolor: '#F1F5F9', borderRadius: 1.5, gap: 0.25 }}>
          {(['all', 'active', 'inactive'] as const).map((m) => {
            const active = statusFilter === m;
            const label = m === 'all' ? 'All' : m === 'active' ? 'Active' : 'Inactive';
            return (
              <Box
                key={m}
                onClick={() => setStatusFilter(m)}
                sx={{
                  px: 1.5,
                  py: 0.625,
                  borderRadius: 1,
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: active ? '#0F172A' : '#64748B',
                  bgcolor: active ? '#FFFFFF' : 'transparent',
                  boxShadow: active ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': { color: '#0F172A' },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            displayEmpty
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            sx={{
              bgcolor: '#F8FAFC',
              borderRadius: 1.5,
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#0F172A',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4F46E5' },
            }}
          >
            <MenuItem value="all">All Agents</MenuItem>
            {uniqueAgents.map((agent) => (
              <MenuItem key={agent} value={agent}>
                {agent}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        <Chip
          label={`${filteredClients.length} of ${clients.length} shown`}
          size="small"
          sx={{
            fontSize: '0.72rem',
            fontWeight: 600,
            bgcolor: '#EEF2FF',
            color: '#3730A3',
            fontFeatureSettings: '"tnum"',
          }}
        />
      </Paper>

      {/* ------------------------------------------------------------------ */}
      {/* Delete client confirmation — admin / super-admin only               */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={!!clientToDelete}
        onClose={() => !deletingClient && setClientToDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <Box sx={{ pt: 3, pb: 1, textAlign: 'center' }}>
          <Box
            sx={{
              width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
              bgcolor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <DeleteIcon sx={{ fontSize: 28, color: '#DC2626' }} />
          </Box>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0F172A', mb: 0.75 }}>
            Delete {clientToDelete?.name}?
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748B', px: 3, lineHeight: 1.55 }}>
            This permanently removes the client and <b>all their trades, payouts and fund
            history</b>. This action cannot be undone.
          </Typography>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, justifyContent: 'center', gap: 1 }}>
          <Button
            onClick={() => setClientToDelete(null)}
            disabled={deletingClient}
            variant="outlined"
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', borderRadius: 1.5, px: 3,
              borderColor: '#E2E8F0', color: '#475569',
              '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteClient}
            disabled={deletingClient}
            variant="contained"
            startIcon={deletingClient ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', borderRadius: 1.5, px: 3,
              bgcolor: '#DC2626', boxShadow: 'none',
              '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
            }}
          >
            {deletingClient ? 'Deleting…' : 'Delete client'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbars                                                           */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: '8px' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ borderRadius: '8px' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity="info"
          icon={<ConstructionIcon />}
          sx={{
            width: '100%',
            background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
            border: '1px solid #F59E0B',
            color: '#92400E',
            '& .MuiAlert-icon': {
              color: '#D97706',
            },
          }}
        >
          {message}
        </Alert>
      </Snackbar>

      {/* ------------------------------------------------------------------ */}
      {/* AG-Grid                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid #E2E8F0',
          bgcolor: '#FFFFFF',
          overflow: 'hidden',
        }}
      >
        <Box
          className="ag-theme-alpine"
          sx={{
            height: 560,
            width: '100%',
            // Premium palette — override AG-Grid's alpine defaults
            '--ag-background-color': '#FFFFFF',
            '--ag-odd-row-background-color': '#FFFFFF',
            '--ag-header-background-color': '#F8FAFC',
            '--ag-header-foreground-color': '#64748B',
            '--ag-row-hover-color': '#F8FAFC',
            '--ag-selected-row-background-color': '#EEF2FF',
            '--ag-border-color': '#E2E8F0',
            '--ag-row-border-color': '#E2E8F0',
            '--ag-font-family': 'Inter, -apple-system, system-ui, sans-serif',
            '--ag-font-size': '13px',
            '--ag-grid-size': '4px',
            '--ag-list-item-height': '36px',
            '--ag-cell-horizontal-padding': '14px',
            '& .ag-header': {
              borderBottom: '1px solid #E2E8F0',
            },
            '& .ag-header-cell': {
              fontSize: '0.68rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            },
            '& .ag-header-cell-label': {
              fontWeight: 600,
              color: '#64748B',
            },
            '& .ag-row': {
              cursor: 'pointer',
              borderBottom: '1px solid #F1F5F9',
              transition: 'background-color 0.12s ease',
            },
            '& .ag-cell': {
              display: 'flex',
              alignItems: 'center',
              color: '#0F172A',
              fontFeatureSettings: '"tnum"',
            },
            '& .ag-root-wrapper': {
              border: 'none',
            },
            '& .ag-overlay-loading-center, & .ag-overlay-no-rows-center': {
              fontSize: '0.85rem',
              color: '#64748B',
              fontWeight: 500,
              bgcolor: 'transparent',
              boxShadow: 'none',
              border: 'none',
            },
          }}
        >
          <AgGridReact<EnrichedClient>
            ref={gridRef}
            rowData={filteredClients}
            columnDefs={columnDefs}
            animateRows
            rowHeight={56}
            headerHeight={40}
            onRowClicked={(event) => {
              if (event.data) {
                navigate(`/clients/${event.data.clientId}`);
              }
            }}
            overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading clients…</span>'
            overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No clients match your filters</span>'
          />
        </Box>
      </Paper>
      </>)}

      {/* ------------------------------------------------------------------ */}
      {/* Create Client Dialog                                                */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" fontWeight={700}>
              Add New Client
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Client Name"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter client name"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Initial Capital"
                fullWidth
                required
                type="number"
                value={formData.initialCapital}
                onChange={(e) => setFormData({ ...formData, initialCapital: e.target.value })}
                placeholder="1000000"
                InputProps={{
                  startAdornment: <InputAdornment position="start">&#8377;</InputAdornment>,
                }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Broker Client Code"
                fullWidth
                value={formData.brokerClientCode}
                onChange={(e) => setFormData({ ...formData, brokerClientCode: e.target.value.toUpperCase().trim() })}
                placeholder="e.g. PERI5510"
                helperText="Optional — broker's client identifier. Used to auto-route trade-book imports (Bajaj .xls)."
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#5E6C84', fontWeight: 600 }}>
                Brokerage Rates
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Equity Delivery (%)"
                fullWidth
                size="small"
                type="number"
                value={formData.stockRate}
                onChange={(e) => setFormData({ ...formData, stockRate: e.target.value })}
                inputProps={{ step: '0.01', min: 0 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Intraday (%)"
                fullWidth
                size="small"
                type="number"
                value={formData.intraStockRate}
                onChange={(e) => setFormData({ ...formData, intraStockRate: e.target.value })}
                inputProps={{ step: '0.001', min: 0 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Futures (%)"
                fullWidth
                size="small"
                type="number"
                value={formData.futureRate}
                onChange={(e) => setFormData({ ...formData, futureRate: e.target.value })}
                inputProps={{ step: '0.001', min: 0 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Options (&#8377;/lot)"
                fullWidth
                size="small"
                type="number"
                value={formData.optionRate}
                onChange={(e) => setFormData({ ...formData, optionRate: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">&#8377;</InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#5E6C84', fontWeight: 600 }}>
                Agent Referral (Optional)
              </Typography>
              <TextField
                select
                fullWidth
                label="Referred By (Agent)"
                value={formData.referredByEmployeeId}
                onChange={(e) => setFormData({ ...formData, referredByEmployeeId: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
              >
                <MenuItem value="">None</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.employeeId} value={emp.employeeId}>
                    {emp.name} - {emp.designation}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {formData.referredByEmployeeId && (
              <Grid item xs={12}>
                <Accordion
                  elevation={0}
                  sx={{
                    border: '1px solid #E0E0E0',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ color: '#5E6C84', fontWeight: 600 }}>
                      Commission Rates (defaults applied)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          label="Delivery (%)"
                          fullWidth
                          size="small"
                          type="number"
                          value={formData.commStockRate}
                          onChange={(e) => setFormData({ ...formData, commStockRate: e.target.value })}
                          inputProps={{ step: '0.01' }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          label="Intraday (%)"
                          fullWidth
                          size="small"
                          type="number"
                          value={formData.commIntradayRate}
                          onChange={(e) => setFormData({ ...formData, commIntradayRate: e.target.value })}
                          inputProps={{ step: '0.01' }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          label="FutStk (%)"
                          fullWidth
                          size="small"
                          type="number"
                          value={formData.commFutStkRate}
                          onChange={(e) => setFormData({ ...formData, commFutStkRate: e.target.value })}
                          inputProps={{ step: '0.01' }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          label="Option (per lot)"
                          fullWidth
                          size="small"
                          type="number"
                          value={formData.commOptionRate}
                          onChange={(e) => setFormData({ ...formData, commOptionRate: e.target.value })}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">&#8377;</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#6B778C' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateClient}
            disabled={creating}
            sx={{
              px: 3,
              textTransform: 'none',
              bgcolor: '#0A0A0A',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#262626',
                boxShadow: 'none',
              },
            }}
          >
            {creating ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Create Client'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ClientsList;
